import * as IBooks from '../../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import * as IReserve from '../../../../../interfaces/reservations';
import {
  ReservationResponseError,
  InvalidReservationErrors,
} from './../../../../../interfaces/reservations';

import * as B from '../../../../reducers/merchants/resources/books';
import * as I from '../../../../reducers/merchants/resources/books/inputs';
import * as a from './api';
import securedAxios from '../../../../../utils/secured-axios';
import { MerchantDetail } from '../../../../../interfaces/merchants';
import { Dispatch } from '@reduxjs/toolkit';
import { buildApiV2Url } from 'src/utils/request-url';

class ReservationError implements Error {
  public name = 'ReservationError';

  constructor(public message: string) {}

  public toString() {
    return this.name + ': ' + this.message;
  }
}

export class RestartReservationError extends ReservationError {
  public name = 'RestartReservationError';
}

export class ReselectDateError extends ReservationError {
  public name = 'ReselectDateError';
}

export class ReinputPaymentError extends ReservationError {
  public name = 'ReinputPaymentError';
}

const convertResponseErrorType = (
  errors: ReservationResponseError,
): IReserve.InvalidReservationErrors | IReserve.BookingFailedReason => {
  if (IReserve.isInvalidReservationError(errors)) {
    return errors;
  }

  if (errors && errors.preferred_time) {
    return IReserve.BookingFailedReason.Occupied;
  }
  if (
    errors &&
    errors.payment_method &&
    errors.payment_method === IReserve.BookingFailedReason.StripeErrorOccurred
  ) {
    return IReserve.BookingFailedReason.StripeErrorOccurred;
  }

  return IReserve.BookingFailedReason.Unexpected;
};

const handleReservationFinishFailure = (
  errors: ReservationResponseError,
  dispatch: I.Dispatch & B.Dispatch,
) => {
  dispatch({
    payload: {
      finishedStatus: IReserve.FinishedStatus.Failed,
      entireError: convertResponseErrorType(errors),
    },
    type: B.Types.SetFinishedStatus,
  });

  if (IReserve.isInvalidReservationError(errors)) {
    throw new RestartReservationError('post api failed');
  }

  // NOTE: 以下のエラーは冗長だが後続の処理を止めるためにthrowする必要があった
  // throwは追えなくなるのでやめるべき

  if (errors && errors.preferred_time) {
    throw new ReselectDateError('post api failed');
  } else if (errors && errors.remotelock_error) {
    dispatch({
      payload: {
        message:
          '予約できませんでした。恐れ入りますが、事業者にご確認ください。',
      },
      type: B.Types.SetRemoteLockErrorDetailsOnCompletion,
    });
  } else if (
    errors &&
    errors.payment_method &&
    errors.payment_method === IReserve.BookingFailedReason.StripeErrorOccurred
  ) {
    if (errors.payment_error_detail) {
      dispatch({
        payload: { message: errors.payment_error_detail.message || '' },
        type: B.Types.SetStripeErrorDetailsOnCompletion,
      });
    }
    throw new ReinputPaymentError('post api failed');
  } else {
    throw new RestartReservationError('post api failed');
  }
};

export const inputConfirmation =
  (params: {
    reservationId: number;
    termsAccepted: boolean | undefined;
    selectedPaymentMethod: IReserve.InputPaymentMethod | undefined;
    lineAccessToken: string | null;
    lineIdToken: string | null;
    merchantPublicId: string;
  }) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    const {
      reservationId,
      termsAccepted,
      selectedPaymentMethod,
      lineAccessToken,
      lineIdToken,
      merchantPublicId,
    } = params;
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    const res = await a.postReservationsFinish({
      reservationId,
      inputs: {
        termsAccepted,
        selectedPaymentMethod,
      },
      lineAccessToken,
      lineIdToken,
      merchantPublicId,
    });
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    if (!res.success) {
      if (res.errorCode === 404) {
        // drifting予約が掃除されて削除された場合。見つからなくなり404になるので擬似的なエラーとして終了させる
        const e: InvalidReservationErrors = {
          details: { base: 'reservation flow expired' },
        };
        handleReservationFinishFailure(e, dispatch);
      } else {
        handleReservationFinishFailure(res.errors!, dispatch);
      }
      return;
    }

    dispatch({ payload: { termsAccepted }, type: I.Types.InputConfirmation });
    dispatch({
      payload: {
        googleCalendarTemplateUrl: res.google_calendar_template_url ?? '',
      },
      type: I.Types.SetGoogleCalendarTemplateUrl,
    });
    dispatch({
      payload: { reservationId, available: res.create_user_with_resv! },
      type: B.Types.SetCreateUserWithResv,
    });
    dispatch({
      payload: {
        finishedStatus: res.entered!.finished_status,
      },
      type: B.Types.SetFinishedStatus,
    });
    dispatch({
      payload: {},
      type: I.Types.ClearReservationId,
    });
  };

export const inputPayment = async (args: {
  reservationId: number;
  payment: IInputs.Payment;
  merchantPublicId: string;
  email: string;
  dispatch: Dispatch;
}) => {
  const { reservationId, payment, merchantPublicId, email, dispatch } = args;
  dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
  dispatch({ payload: {}, type: B.Types.ResetPaymentErrors });
  const merchant = await securedAxios.get<MerchantDetail>(
    buildApiV2Url(`/merchants/${merchantPublicId}`),
  );
  const paymentResponse = await a.postPayment(
    payment,
    merchant.data.jcb_enabled,
  );
  if (paymentResponse.error) {
    dispatch({
      payload: { errors: paymentResponse.error! },
      type: B.Types.SetPaymentErrors,
    });
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    throw new Error('postPayment failed');
  }
  const { data: apiResponse, errorType } = await a.postReservationsPayment({
    reservationId,
    inputs: {
      payment: paymentResponse.payment,
    },
    email,
    merchantPublicId,
  });
  dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
  if (errorType) {
    return { errorType };
  }
  if (apiResponse) {
    dispatch({
      payload: { payment },
      type: I.Types.InputPayment,
    });
    dispatch({
      payload: {
        selectedPaymentMethod: apiResponse.selected_payment_method,
        ctok: paymentResponse.payment.token,
      },
      type: B.Types.SetSelectPaymentMethod,
    });
    return { errorType: undefined };
  }
  throw new Error('Failed input payment normally unreachable');
};

export const resetPaymentMethods =
  (merchantPublicId: string, reservationId: number) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    const res = await a.fetchReservationPaymentMethods(
      merchantPublicId,
      reservationId,
    );
    dispatch({
      payload: { paymentMethods: res.payment_methods },
      type: B.Types.SetPaymentMethods,
    });
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
  };

export const clearUserInfoError = (name: string) => (dispatch: B.Dispatch) => {
  dispatch({ payload: { fieldName: name }, type: B.Types.ResetErrors });
};

const isChangeSurvey = (
  settings: IBooks.SettingUserInfo,
  errors: IReserve.Errors,
): boolean => {
  if (!errors.survey_answer) {
    return false;
  }

  return !settings.survey;
};

const isChangeResourceCustomerFields = (
  settings: IBooks.SettingUserInfo,
  errors: IReserve.Errors,
): boolean => {
  if (!errors.resource_customer_fields) {
    return false;
  }
  const fieldNames = settings.customerFields!.fields.map((f) => f.name);
  const results = Object.keys(errors.resource_customer_fields).map((k) =>
    fieldNames.includes(k),
  );

  return results.includes(false);
};

const isChangeQuestionnaireCustomFields = (
  settings: IBooks.SettingUserInfo,
  errors: IReserve.Errors,
): boolean => {
  if (!errors.questionnaire_custom_fields) {
    return false;
  }
  const fieldNames = settings.questionnaires!.map((f) =>
    f.customFieldId.toString(),
  );
  const results = Object.keys(errors.questionnaire_custom_fields).map((k) =>
    fieldNames.includes(k),
  );

  return results.includes(false);
};

const isChangeCustomForm = (
  settings: IBooks.SettingUserInfo,
  errors: IReserve.Errors,
): boolean =>
  isChangeSurvey(settings, errors) ||
  isChangeResourceCustomerFields(settings, errors) ||
  isChangeQuestionnaireCustomFields(settings, errors);

export const inputUserInfo = async (params: {
  reservationId: number;
  userInfo: IInputs.UserInfo;
  settings: IBooks.SettingUserInfo;
  merchantPublicId: string;
  dispatch: Dispatch;
}) => {
  const { reservationId, userInfo, settings, merchantPublicId, dispatch } =
    params;
  const res = await a.postReservationsUserInfo(
    reservationId,
    { userInfo },
    merchantPublicId,
  );
  const { data, success, errorCode, errorType, errors } = res;
  if (!success) {
    if (errorCode === 400 && errorType === 'above_reservation_count_limit') {
      return {
        errorType:
          'above_reservation_count_limit' as a.PostReservationUserInfoErrorType,
      };
    } else if (errorCode === 400 && errorType === 'customer_blocking') {
      return {
        errorType: 'customer_blocking' as a.PostReservationUserInfoErrorType,
      };
    } else if (errorCode === 400 && errorType === 'invalid_input' && errors) {
      return {
        errorType: 'invalid_input' as a.PostReservationUserInfoErrorType,
        errors: errors,
      };
    } else if (
      errorCode === 400 &&
      data?.errors &&
      !IReserve.isInvalidReservationError(data.errors) &&
      !isChangeCustomForm(settings, data.errors)
    ) {
      dispatch({ payload: { errors: data.errors! }, type: B.Types.SetErrors });
      throw new Error('post api failed');
    } else {
      dispatch({
        payload: {
          finishedStatus: IReserve.FinishedStatus.Failed,
          entireError: IReserve.BookingFailedReason.Unexpected,
        },
        type: B.Types.SetFinishedStatus,
      });
      throw new RestartReservationError('post api failed');
    }
  }
  dispatch({
    payload: { paymentMethods: data?.entered!.payment_methods },
    type: B.Types.SetPaymentMethods,
  });
  // お支払い設定がある場合
  if (
    data?.entered?.payment_methods &&
    data?.entered.payment_methods[0].method !== 'unspecified'
  ) {
    const availablePaymentMethods = data?.entered.payment_methods.filter(
      (v) => v.available,
    );
    if (availablePaymentMethods.length > 0) {
      dispatch({
        payload: { payment: { paymentMethod: availablePaymentMethods[0] } },
        type: I.Types.InputPayment,
      });
    }
  }
  // お支払い設定がない場合(金額表示に必要)
  if (
    data?.entered?.payment_methods &&
    data?.entered.payment_methods[0].method === 'unspecified'
  ) {
    dispatch({
      payload: {
        payment: { paymentMethod: data?.entered!.payment_methods[0] },
      },
      type: I.Types.InputPayment,
    });
  }
  dispatch({
    payload: { userInfo },
    type: I.Types.InputUserInfo,
  });
  return {
    errorType: undefined,
  };
};

export const inputBookingDateTime =
  (
    reservationId: number,
    timeSlotAvailability: IInputs.TimeSlotAvailability,
    merchantPublicId: string,
  ) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    const res = await a.postReservationsBookingDateTime(
      reservationId,
      {
        timeSlotAvailability,
      },
      merchantPublicId,
    );
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    if (!res.success) {
      dispatch({
        payload: {
          finishedStatus: IReserve.FinishedStatus.Failed,
          entireError: IReserve.BookingFailedReason.Unexpected,
        },
        type: B.Types.SetFinishedStatus,
      });
      throw new RestartReservationError('post api failed');
    }

    dispatch({
      payload: { timeSlotAvailability },
      type: I.Types.InputBookingDateTime,
    });
  };

export const inputBookingSchoolTimeSlot =
  (
    reservationId: number,
    schoolTimeSlots: IInputs.SchoolTimeSlot[],
    merchantPublicId: string,
  ) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    const res = await a.postReservationsTimeSlots(
      reservationId,
      {
        schoolTimeSlots,
      },
      merchantPublicId,
    );
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    if (!res.success) {
      dispatch({
        payload: {
          finishedStatus: IReserve.FinishedStatus.Failed,
          entireError: IReserve.BookingFailedReason.Unexpected,
        },
        type: B.Types.SetFinishedStatus,
      });
      throw new RestartReservationError('post api failed');
    }

    dispatch({
      payload: { schoolTimeSlots },
      type: I.Types.InputBookingSchoolTimeSlot,
    });
  };

export const clearBookingSchoolTimeSlot =
  () => async (dispatch: I.Dispatch) => {
    dispatch({
      payload: {},
      type: I.Types.ClearBookingSchoolTimeSlot,
    });
  };

export const inputBookingEventTimeSlot =
  (
    modelType: IBooks.ModelType,
    merchantId: string,
    resourceId: string,
    eventTimeSlot: IInputs.EventTimeSlot,
    withExpressFlow: boolean,
    reservationId?: number,
  ) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    // ここでreservationIdをセットしている
    const res = await a.postReservationsStart(
      modelType,
      merchantId,
      resourceId,
      {
        eventTimeSlot,
      },
      withExpressFlow,
      reservationId,
    );
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    if (!res.success) {
      dispatch({
        payload: {
          finishedStatus: IReserve.FinishedStatus.Failed,
          entireError: IReserve.BookingFailedReason.Unexpected,
        },
        type: B.Types.SetFinishedStatus,
      });
      throw new RestartReservationError('post api failed');
    }

    dispatch({
      payload: { reservationId: res.entered!.reservation_id },
      type: I.Types.InputReservationId,
    });

    dispatch({
      payload: { eventTimeSlot },
      type: I.Types.InputBookingEventTimeSlot,
    });
  };

export const clearBookingTargets =
  (targets: { course: boolean; options: boolean; staff?: boolean }) =>
  (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({
      payload: {
        clearCourse: targets.course,
        clearOptions: targets.options,
        clearStaff: targets.staff,
      },
      type: I.Types.ClearBookingTarget,
    });
    dispatch({
      payload: { paymentMethods: [] },
      type: B.Types.SetPaymentMethods,
    });
  };

type InputBookingTargets =
  | InputBookingTargetsBasic
  | InputBookingTargetsWithStaff
  | InputBookingTargetsLesson;

interface InputBookingTargetsBasic {
  course: IBooks.Course;
  options?: IBooks.Option[];
}

interface InputBookingTargetsWithStaff {
  course: IBooks.Course;
  options?: IBooks.Option[];
  staff: IBooks.Staff;
  selected: IBooks.Switch;
}

interface InputBookingTargetsLesson {
  lesson: IBooks.Lesson;
}

const isLesson = (
  targets: InputBookingTargets,
): targets is InputBookingTargetsLesson => !!targets.hasOwnProperty('lesson');

const isWithStaff = (
  targets: InputBookingTargets,
): targets is InputBookingTargetsWithStaff => !!targets.hasOwnProperty('staff');

const isBasic = (
  targets: InputBookingTargets,
): targets is InputBookingTargetsBasic =>
  !isLesson(targets) && !isWithStaff(targets);

const createInputBookingTargetPayload = (targets: InputBookingTargets) => {
  if (isBasic(targets)) {
    return { course: targets.course, options: targets.options };
  }
  if (isWithStaff(targets)) {
    return {
      course: targets.course,
      options: targets.options,
      staff: targets.staff,
    };
  }
  if (isLesson(targets)) {
    return { lesson: targets.lesson };
  }
  throw new Error();
};

export const inputBookingTargets =
  (
    modelType: IBooks.ModelType,
    merchantId: string,
    resourceId: string,
    targets: InputBookingTargets,
    withExpressFlow: boolean,
    reservationId?: number,
  ) =>
  async (dispatch: I.Dispatch & B.Dispatch) => {
    dispatch({ payload: { postable: false }, type: B.Types.SetPostable });
    const res = await a.postReservationsStart(
      modelType,
      merchantId,
      resourceId,
      targets,
      withExpressFlow,
      reservationId,
    );
    dispatch({ payload: { postable: true }, type: B.Types.SetPostable });
    if (!res.success) {
      dispatch({
        payload: {
          finishedStatus: IReserve.FinishedStatus.Failed,
          entireError: IReserve.BookingFailedReason.Unexpected,
        },
        type: B.Types.SetFinishedStatus,
      });
      throw new RestartReservationError('post api failed');
    }
    dispatch({
      payload: { reservationId: res.entered!.reservation_id },
      type: I.Types.InputReservationId,
    });
    dispatch({
      payload: createInputBookingTargetPayload(targets),
      type: I.Types.InputBookingTarget,
    });
    if (isWithStaff(targets)) {
      dispatch({
        payload: { firstSelected: targets.selected },
        type: B.Types.SetFirstSelected,
      });
    }
  };
